import {useIssueState} from '@github-ui/issue-pr-state'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {ActionList} from '@primer/react'
import type React from 'react'

import {useParentIssueSubscription} from '../../ParentIssueSubscription'
import type {ParentIssueNode, ParentIssueRepositoryNode} from './parent-issue-query-types'
import {SubIssuesSummary} from './SubIssuesSummary'

// The parent node carries the union of every fragment's parent selection, so we Pick out just the fields
// this component reads — including `subIssuesSummary`, which is forwarded to the (plain-props) `SubIssuesSummary` child.
// RELAY-CLEANUP(RelationshipsSection): `RelayCompat` widens nullable fields so the still-Relay
// RelationshipsSection parent data is assignable. Remove the wrapper when RelationshipsSection leaves Relay.
export type ParentIssueFragment = RelayCompat<
  Pick<ParentIssueNode, 'id' | 'number' | 'state' | 'stateReason' | 'subIssuesSummary' | 'titleHTML' | 'url'> & {
    readonly repository: Pick<ParentIssueRepositoryNode, 'nameWithOwner'>
  }
>

type ParentIssueProps = {
  issue: ParentIssueFragment
  onLinkClick?: (event: MouseEvent) => void
}

export function ParentIssue({issue: data, onLinkClick}: ParentIssueProps) {
  useParentIssueSubscription(data.id)
  const {sourceIcon} = useIssueState({
    state: data.state,
    stateReason: data.stateReason,
  })

  const IconComponent = sourceIcon('Issue')
  /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side */
  const title = data.titleHTML as SafeHTMLString

  return (
    <ActionList.LinkItem
      href={data.url}
      target="_blank"
      onClick={
        onLinkClick
          ? (event: React.MouseEvent<HTMLElement>) => {
              onLinkClick(event.nativeEvent)
            }
          : undefined
      }
    >
      <ActionList.LeadingVisual>
        {/* eslint-disable-next-line react-hooks/static-components */}
        <IconComponent />
      </ActionList.LeadingVisual>
      <ActionList.TrailingVisual>
        <SubIssuesSummary issue={data} />
      </ActionList.TrailingVisual>
      <VerifiedHTMLText html={title} className="markdown-title" />
      <ActionList.Description variant="block">
        {data.repository.nameWithOwner}#{data.number}
      </ActionList.Description>
    </ActionList.LinkItem>
  )
}
